import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Icon from '../../../../components/AppIcon';
import { Card, CardContent } from '../../../../components/ui/Card';
import { useScheduleTemplates } from '../../../../hooks/useScheduling';
import {
  createShiftSet,
  createRecurringRule,
  assignUsersToShiftSet,
  deleteShiftSet,
  timeToMinutes,
} from '../../../../api/Attendance/scheduling';
import { notifyError } from '../../../../utils/errors';
import { FetchUsers } from '../../../../api/General/account';
import FilterBar, { INITIAL_FILTERS, RecurringScheduleFilters } from './FilterBar';
import ScheduleTable, { parseRRule, minutesToTime, EditRowData } from './ScheduleTable';
import { todayLocalDateParam } from '@/utils/apiDate';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import { describeScheduleSaveError, extractScheduleConflicts, groupScheduleConflicts, isNoResponse } from '../scheduleSaveError';
import { ScheduleSaveErrorDialog, type ScheduleSaveErrorDetails } from '../components/ScheduleSaveErrorDialog';
import { getGoverningRule } from '../governingRule';
import { GAP_CANCELLED, saveRuleConfirmingSplit, SPLIT_CANCELLED, useSplitConfirm } from '../splitConfirm';
import { isPastStart, useSchedulePreSave } from '../schedulePreSave';

const RecurringSchedules: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const company = useSelector((state: any) => state.user.company);
  const role = useSelector((state: any) => state.user.role);
  const userCompanyId = company?.id;
  const canManageSchedules = ['Hr', 'Admin', 'Manager', 'Chief'].includes(role);
  const { toast, showToast, dismissToast } = useToast();
  const { ask: askSplit, askGap, dialog: splitDialog } = useSplitConfirm();
  const { askPastStart, askName, dialogs: preSaveDialogs } = useSchedulePreSave();
  const [saveRefusal, setSaveRefusal] = useState<ScheduleSaveErrorDetails | null>(null);

  // Data
  const { templates, loading, refresh } = useScheduleTemplates();
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Filters
  const [filters, setFilters] = useState<RecurringScheduleFilters>(INITIAL_FILTERS);

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  // The rule the open editor was filled from; the save writes that same rule.
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [editData, setEditData] = useState<EditRowData | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Sort state
  const [sortField, setSortField] = useState<string>('title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Create state (stub for Task 9)
  const [isCreating, setIsCreating] = useState(false);
  const [createData, setCreateData] = useState<EditRowData | null>(null);
  const [createSaving, setCreateSaving] = useState(false);

  // ── Load users (same pattern as CreateScheduleDrawer.jsx) ──

  useEffect(() => {
    if (!userCompanyId) return;
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await FetchUsers('Company', userCompanyId, false);
        if (response?.payload) {
          const users = response.payload.map((user: any) => {
              // Normalize department to string — API may return object {id, name, ...}
              const dept = typeof user?.departmentName === 'string'
                ? user.departmentName
                : (typeof user?.department === 'string'
                    ? user.department
                    : (user?.department?.name || user?.department?.title || ''));
              return {
                id: user.id,
                name: user.fullName || user.userName,
                fullName: user.fullName,
                department: dept,
              };
            });
          users.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
          setAvailableUsers(users);
        }
      } catch (err) {
        notifyError(err);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [userCompanyId]);

  // ── Extract departments from users ──

  const departments = useMemo(() => {
    const set = new Set(availableUsers.map((u: any) => u.department).filter(Boolean));
    return Array.from(set).sort();
  }, [availableUsers]);

  // ── Filter logic ──

  const filteredTemplates = useMemo(() => {
    let result = templates.filter((tmpl) => {
      const rule = getGoverningRule(tmpl.recurringRules);
      if (!rule) return false;
      const parsed = parseRRule(rule.rRule);

      // Title
      if (filters.title && !tmpl.name.toLowerCase().includes(filters.title.toLowerCase()))
        return false;

      // Days (ANY match)
      if (filters.days.length > 0 && !filters.days.some((d) => parsed.days.includes(d)))
        return false;

      // Time overlap — handles both overnight shifts AND overnight filter windows
      if (filters.timeFrom || filters.timeTo) {
        const filterFromMin = filters.timeFrom ? timeToMinutes(filters.timeFrom) : 0;
        const filterToMin = filters.timeTo ? timeToMinutes(filters.timeTo) : 24 * 60;

        const shiftSegments =
          rule.endTimeOfDayMinutes <= rule.startTimeOfDayMinutes
            ? [
                [rule.startTimeOfDayMinutes, 1440],
                [0, rule.endTimeOfDayMinutes],
              ]
            : [[rule.startTimeOfDayMinutes, rule.endTimeOfDayMinutes]];

        const filterSegments =
          filterFromMin > filterToMin
            ? [
                [filterFromMin, 1440],
                [0, filterToMin],
              ]
            : [[filterFromMin, filterToMin]];

        const overlaps = shiftSegments.some(([sStart, sEnd]) =>
          filterSegments.some(([fStart, fEnd]) => sStart < fEnd && sEnd > fStart),
        );
        if (!overlaps) return false;
      }

      // Status
      if (filters.status === 'active' && tmpl.status !== 'Active') return false;
      if (filters.status === 'inactive' && tmpl.status !== 'Inactive') return false;

      // Department
      if (filters.departmentIds.length > 0) {
        const userDepts = (tmpl.assignedUsers || []).map((u: any) => u.department);
        if (!filters.departmentIds.some((d) => userDepts.includes(d))) return false;
      }

      // User
      if (filters.userIds.length > 0) {
        if (!filters.userIds.some((uid) => (tmpl.assignedUserIds || []).includes(uid)))
          return false;
      }

      return true;
    });

    // Sort
    result = [...result].sort((a, b) => {
      let cmp = 0;
      const ruleA = getGoverningRule(a.recurringRules);
      const ruleB = getGoverningRule(b.recurringRules);
      switch (sortField) {
        case 'title':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'status':
          cmp = a.status.localeCompare(b.status);
          break;
        case 'startTime':
          cmp =
            (ruleA?.startTimeOfDayMinutes || 0) - (ruleB?.startTimeOfDayMinutes || 0);
          break;
        case 'interval': {
          const intA = parseInt(ruleA?.rRule?.match(/INTERVAL=(\d+)/)?.[1] || '1', 10);
          const intB = parseInt(ruleB?.rRule?.match(/INTERVAL=(\d+)/)?.[1] || '1', 10);
          cmp = intA - intB;
          break;
        }
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [templates, filters, sortField, sortDir]);

  // ── Edit handlers ──

  const handleEditStart = useCallback(
    (templateId: number) => {
      const template = templates.find((tmpl) => tmpl.id === templateId);
      if (!template) return;
      const rule = getGoverningRule(template.recurringRules);
      if (!rule) return;
      const parsed = parseRRule(rule.rRule);

      setEditingId(templateId);
      setEditingRuleId(rule.id);
      setEditData({
        name: template.name,
        description: template.description || '',
        isActive: template.status === 'Active',
        days: parsed.days,
        interval: parsed.interval,
        startTime: minutesToTime(rule.startTimeOfDayMinutes),
        endTime: minutesToTime(rule.endTimeOfDayMinutes),
        breakStart:
          rule.breakStartTimeOfDayMinutes != null
            ? minutesToTime(rule.breakStartTimeOfDayMinutes)
            : '',
        breakEnd:
          rule.breakEndTimeOfDayMinutes != null
            ? minutesToTime(rule.breakEndTimeOfDayMinutes)
            : '',
        breakDuration: rule.breakDurationMinutes ?? null,
        userIds: template.assignedUserIds || [],
        startDate: rule.startDateUtc ? rule.startDateUtc.slice(0, 10) : '',
        endDate: rule.endDateUtc && !rule.endDateUtc.startsWith('9999') ? rule.endDateUtc.slice(0, 10) : '',
      });
      setIsCreating(false);
      setCreateData(null);
    },
    [templates],
  );

  // A refusal that names employees gets the dialog, which also names their other schedule (CO-996: the owner's
  // wording). A toast cannot hold that list; anything else stays a toast.
  const showSaveRefusal = useCallback((err: unknown, message: string) => {
    const employees = groupScheduleConflicts(extractScheduleConflicts(err), availableUsers);
    if (employees.length > 0) setSaveRefusal({ summary: message, employees });
    else showToast(message, 'error', 12000);
  }, [availableUsers, showToast]);

  const handleEditSave = useCallback(async () => {
    if (!editingId || !editData) return;
    // Both are YYYY-MM-DD here, so a string compare is a date compare.
    if (editData.endDate && editData.startDate && editData.endDate < editData.startDate) {
      showToast(t('ScheduleEndDateBeforeStart'), 'error');
      return;
    }
    setEditSaving(true);
    // CO-996: the title, description and employees go with the rule, so a refused save changes nothing. True only
    // when the server asked for the employees to be written first (see DeferredUsers) and they were.
    let usersSaved = false;
    // CO-1021: the new name of another schedule, renamed from the same-name dialog before this save was sent.
    let otherRenamedTo = '';
    try {
      const template = templates.find((tmpl) => tmpl.id === editingId);
      if (!template) throw new Error('Template not found');
      const rule = template.recurringRules.find((r) => r.id === editingRuleId);
      if (!rule) throw new Error('Rule not found');

      // CO-1021: a title changed to another schedule's name asks for a different name for either of the two.
      let scheduleTitle = editData.name;
      const typedTitle = editData.name.trim();
      if (typedTitle && typedTitle !== (template.name || '').trim()) {
        const named = await askName({ name: typedTitle, schedules: templates, selfId: editingId });
        if (!named) return;
        if (named.name !== typedTitle) {
          const chosen = named.name;
          scheduleTitle = chosen;
          setEditData((prev) => (prev ? { ...prev, name: chosen } : prev));
        }
        otherRenamedTo = named.renamedOther?.name || '';
      }

      const nameChanged = scheduleTitle !== template.name;
      const descChanged = editData.description !== (template.description || '');
      const usersChanged =
        JSON.stringify([...editData.userIds].sort()) !==
        JSON.stringify([...(template.assignedUserIds || [])].sort());
      // Build rRule string
      const rRule = `FREQ=WEEKLY;BYDAY=${editData.days.join(',')};INTERVAL=${editData.interval}`;
      const startMin = timeToMinutes(editData.startTime);
      const endMin = timeToMinutes(editData.endTime);

      // CO-957: a change that would split the schedule is confirmed first (it ends this one and adds another).
      const saved = await saveRuleConfirmingSplit(rule.id, {
        shiftSetId: editingId,
        rRule,
        startTimeOfDayMinutes: startMin,
        endTimeOfDayMinutes: endMin,
        breakStartTimeOfDayMinutes: editData.breakStart
          ? timeToMinutes(editData.breakStart)
          : null,
        breakEndTimeOfDayMinutes: editData.breakEnd
          ? timeToMinutes(editData.breakEnd)
          : null,
        breakDurationMinutes: editData.breakDuration,
        startDateUtc: editData.startDate || rule.startDateUtc,
        // A cleared end date is sent as null: the server reads null as open-ended. Re-sending the
        // rule's old end date here made the field impossible to remove.
        endDateUtc: editData.endDate || null,
        ...(nameChanged ? { scheduleName: scheduleTitle.trim() } : {}),
        ...(descChanged ? { scheduleDescription: editData.description || '' } : {}),
      }, { ask: askSplit, askGap }, usersChanged ? {
        userIds: editData.userIds,
        write: async () => {
          await assignUsersToShiftSet(editingId, editData.userIds);
          usersSaved = true;
        },
      } : undefined);
      if (saved === SPLIT_CANCELLED || saved === GAP_CANCELLED) {
        // The rule was not saved; the editor stays open with what the user typed. Employees the server asked to be
        // written first are said, and shown under the editor.
        let message = t(saved === GAP_CANCELLED ? 'ScheduleGapCancelled' : 'ScheduleSplitCancelled');
        if (usersSaved) message = `${message} ${t('ScheduleRuleNotSavedUsersSaved')}`;
        if (otherRenamedTo) message = `${message} ${t('ScheduleOtherRenamedNote', { name: otherRenamedTo })}`;
        showToast(message, usersSaved ? 'error' : 'info', usersSaved ? 12000 : undefined);
        if (usersSaved || otherRenamedTo) refresh();
        return;
      }

      setEditingId(null);
      setEditData(null);
      await refresh();
    } catch (err) {
      notifyError(err);
      let message = describeScheduleSaveError(err, t, availableUsers, t('CouldntSaveSchedule'), usersSaved);
      if (usersSaved) message = `${message} ${t('ScheduleRuleNotSavedUsersSaved')}`;
      if (otherRenamedTo) message = `${message} ${t('ScheduleOtherRenamedNote', { name: otherRenamedTo })}`;
      showSaveRefusal(err, message);
      // Show what did land (the employee list, the other schedule's name) under the still-open editor.
      if (usersSaved || otherRenamedTo) refresh();
    } finally {
      setEditSaving(false);
    }
  }, [editingId, editingRuleId, editData, templates, refresh, t, availableUsers, showSaveRefusal, showToast, askSplit, askGap, askName]);

  const handleEditCancel = useCallback(() => {
    setEditingId(null);
    setEditData(null);
  }, []);

  // ── Archive handler ──

  const handleArchive = useCallback(
    async (templateId: number) => {
      if (!window.confirm(t('ConfirmArchive'))) return;
      try {
        await deleteShiftSet(templateId, { cascade: true });
        await refresh();
      } catch (err) {
        notifyError(err);
        showToast(describeScheduleSaveError(err, t, availableUsers, t('FailedToDeleteRecurringSchedule')), 'error');
      }
    },
    [t, refresh, availableUsers, showToast],
  );

  // ── Create handlers ──

  const handleCreateStart = useCallback(() => {
    setEditingId(null);
    setEditData(null);
    setIsCreating(true);
    setCreateData({
      name: '',
      description: '',
      isActive: true,
      days: [],
      interval: 1,
      startTime: '',
      endTime: '',
      breakStart: '',
      breakEnd: '',
      breakDuration: null,
      userIds: [],
      startDate: todayLocalDateParam(),
      endDate: '',
    });
  }, []);

  const handleCreateSave = useCallback(async () => {
    if (!createData) return;
    if (createData.endDate && createData.startDate && createData.endDate < createData.startDate) {
      showToast(t('ScheduleEndDateBeforeStart'), 'error');
      return;
    }
    setCreateSaving(true);
    // The set is created first (the rule needs its id). If the rule is then refused, delete it again,
    // as the drawer does, so "not saved" is true and a retry doesn't leave a second same-named set.
    let createdSetId: number | null = null;
    // CO-1021: the new name of another schedule, renamed from the same-name dialog before this save was sent.
    let otherRenamedTo = '';
    let scheduleTitle = createData.name.trim();
    try {
      // CO-1021: the same two questions as the drawer's create new, past start first. See schedulePreSave.tsx.
      if (createData.userIds.length > 0 && isPastStart(createData.startDate)
        && !(await askPastStart(createData.startDate, createData.userIds.length))) return;
      if (scheduleTitle) {
        const named = await askName({ name: scheduleTitle, schedules: templates, selfId: null });
        if (!named) return;
        if (named.name !== scheduleTitle) {
          const chosen = named.name;
          scheduleTitle = chosen;
          setCreateData((prev) => (prev ? { ...prev, name: chosen } : prev));
        }
        otherRenamedTo = named.renamedOther?.name || '';
      }

      // Create ShiftSet
      const setResult = await createShiftSet({
        name: scheduleTitle,
        description: createData.description,
        assignedUserIds: createData.userIds,
        embeddedSingleShifts: [],
        ruleItems: [],
      });

      if (!setResult?.id) throw new Error('Failed to create schedule');
      createdSetId = setResult.id;

      // Build rRule
      const rRule = `FREQ=WEEKLY;BYDAY=${createData.days.join(',')};INTERVAL=${createData.interval}`;
      const startMin = timeToMinutes(createData.startTime);
      const endMin = timeToMinutes(createData.endTime);

      // Create RecurringShiftRule
      await createRecurringRule({
        shiftSetId: setResult.id,
        rRule,
        startTimeOfDayMinutes: startMin,
        endTimeOfDayMinutes: endMin,
        breakStartTimeOfDayMinutes: createData.breakStart ? timeToMinutes(createData.breakStart) : null,
        breakEndTimeOfDayMinutes: createData.breakEnd ? timeToMinutes(createData.breakEnd) : null,
        breakDurationMinutes: createData.breakDuration,
        startDateUtc: createData.startDate ? new Date(createData.startDate).toISOString() : new Date().toISOString(),
        endDateUtc: createData.endDate ? new Date(createData.endDate).toISOString() : undefined,
      } as any);

      setIsCreating(false);
      setCreateData(null);
      await refresh();
    } catch (err) {
      notifyError(err);
      // No answer (offline, timeout): the server may still be creating the set or the rule, and deleting
      // the set now could leave that rule linked to an archived set. Leave it, reload and ask the user to look.
      if (isNoResponse(err)) {
        await refresh();
        showToast(t('ScheduleSaveOutcomeUnknown'), 'error', 12000);
        return;
      }
      let cleanupNote = '';
      if (createdSetId) {
        try {
          await deleteShiftSet(createdSetId, { cascade: true });
        } catch {
          // The empty set stays; say so, or a retry quietly makes a second one with the same name.
          cleanupNote = ` ${t('ScheduleEmptySetMayRemain', { name: scheduleTitle })}`;
          await refresh();
        }
      }
      const renamedNote = otherRenamedTo ? ` ${t('ScheduleOtherRenamedNote', { name: otherRenamedTo })}` : '';
      if (otherRenamedTo) await refresh();
      showSaveRefusal(err, describeScheduleSaveError(err, t, availableUsers, t('CouldntSaveSchedule')) + cleanupNote + renamedNote);
    } finally {
      setCreateSaving(false);
    }
  }, [createData, templates, refresh, t, availableUsers, showSaveRefusal, showToast, askPastStart, askName]);

  const handleCreateCancel = useCallback(() => {
    setIsCreating(false);
    setCreateData(null);
  }, []);

  // ── Sort handler ──

  const handleSort = useCallback(
    (field: string) => {
      if (field === sortField) {
        setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDir('asc');
      }
    },
    [sortField],
  );

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/attendance/work-schedules')}
            className="p-2 rounded-md hover:bg-muted transition-colors"
            title={t('Back')}
            data-testid="recurring-schedules-back"
          >
            <Icon name="ArrowLeft" size={20} />
          </button>
          <h1 className="text-2xl font-bold text-foreground">
            {t('RecurringSchedules')}
          </h1>
        </div>
        {canManageSchedules && (
          <button
            onClick={handleCreateStart}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
            data-testid="recurring-schedules-create-new"
          >
            {t('CreateNew', { defaultValue: 'Create New' })}
          </button>
        )}
      </div>

      {/* Filter bar */}
      <Card variant="elevated">
        <CardContent className="p-4">
          <FilterBar
            filters={filters}
            onFiltersChange={setFilters}
            departments={departments}
            users={availableUsers}
          />
        </CardContent>
      </Card>

      {/* Result count */}
      {templates.length > 0 && (
        <p className="text-sm text-muted-foreground" data-testid="result-count">
          {t('ShowingXofY', {
            shown: filteredTemplates.length,
            total: templates.length,
          })}
        </p>
      )}

      {/* Table */}
      <Card variant="elevated">
        <CardContent className="p-0">
          {loading || loadingUsers ? (
            <div className="flex items-center justify-center py-12">
              <Icon
                name="Loader2"
                size={24}
                className="animate-spin text-muted-foreground"
              />
            </div>
          ) : (
            <ScheduleTable
              templates={filteredTemplates}
              allUsers={availableUsers}
              departments={departments}
              canManageSchedules={canManageSchedules}
              onEditStart={handleEditStart}
              onArchive={handleArchive}
              editingId={editingId}
              editData={editData}
              onEditChange={setEditData}
              onEditSave={handleEditSave}
              onEditCancel={handleEditCancel}
              editSaving={editSaving}
              isCreating={isCreating}
              createData={createData}
              onCreateChange={setCreateData}
              onCreateSave={handleCreateSave}
              onCreateCancel={handleCreateCancel}
              createSaving={createSaving}
              sortField={sortField}
              sortDir={sortDir}
              onSort={handleSort}
              totalCount={templates.length}
              onClearFilters={() => setFilters(INITIAL_FILTERS)}
            />
          )}
        </CardContent>
      </Card>
      {splitDialog}
      {preSaveDialogs}
      <ScheduleSaveErrorDialog open={!!saveRefusal} details={saveRefusal} onClose={() => setSaveRefusal(null)} />
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default RecurringSchedules;
